import useAppStore from "@/context/store";
import { gdOssUrl } from "@/utils/imageUrl";
import useRequest from "@/utils/useReq";
import { useCallback, useMemo, useState } from "react";
import { Case, Switch } from "react-if";
import { getCertList, getVipGoods, updateCert } from "../../../services";
import AuthSubject from "../../AuthSubject/form";
import HBMessage from "../../HBMessage";
import HBModal from "../../HBModal";
import PayResult from "../payResult";
import ScrollWarp from "../shared/scrollWrap";
import SubjectConfirm from "../subjectConfirm";
import styles from "./index.module.less";
import VipDetail from "./vipDetail";

/**
 * 打开会员弹框
 * @typedef {(step: 'overview'|'pay'|'choice')=>any} Cancel
 * @param {Object} props
 * @param {(order: Object)=>any} props.onFinish 流程结束，且订单已完成， 授权主体有没有填写未知
 * @param {(step: 'vipDetail' )=> any} props.onAborted 订单未支付,流程中止
 * @param {(order: Object)=>any} props.onPaid 订单支付完成，但流程还在继续
 * @param {function} props.onOK 所有流程完成,且 且弹框关闭
 * @param {Cancel} props.onCancel 弹框关闭 参数 step 当前页面的位置
 */
export default function BuyExpVipDialog({
  onOk,
  onCancel,
  onPaid,
  onFinish,
  onAborted,
  ...props
}) {
  const { data: expVipGood } = useRequest(async () => {
    const { list, err } = await getVipGoods();
    if (err) {
      return null;
    }

    return list.find(({ code }) => code === "huabantiyanvip");
  });

  const { user, setUser } = useAppStore(({ user, setUser }) => ({
    user,
    setUser,
  }));
  // 步骤
  const [step, setStep] = useState("vipDetail");

  const [vipType] = useState("personal");

  const [order, setOrder] = useState({});
  const [subject, setSubject] = useState({});

  const next = useCallback(
    (params) => {
      if (step === "payResult") setStep("authSubject");
      if (step === "authSubject") {
        setSubject(params);
        setStep("subjectConfirm");
      }
    },
    [step]
  );

  const onOrderDone = useCallback(
    (order) => {
      onPaid && onPaid(order);
      setOrder(order);
      setStep("payResult");

      // 更新用户正确信息
      setUser({
        ...user,
        extra: {
          ...(user.extra || {}),
          material_vip: 5,
          material_vip_expire_at:
            new Date(order.updated_at).getTime() + 3 * 24 * 3600 * 1000,
        },
      });
    },
    [setUser, onPaid, user]
  );

  const onSubmit = useCallback(async () => {
    const certSubject = await getCertList({
      source_no: order.order_no,
    });

    if (!certSubject?.list?.length > 0) {
      HBMessage.error("获取授权信息失败");
      return;
    }

    const result = await updateCert({
      id: certSubject.list[0].id,
      subject,
    });

    if (result.err) return;

    HBMessage.success("提交成功");
    onOk && onOk();
  }, [onOk, order, subject]);

  const wrapStyle = useMemo(() => {
    return {
      width: step === "vipDetail" ? 664 : 960,
      height: step === "vipDetail" ? 450 : 772,
      maxHeight: "100vh",
      overflow: "hidden",
      backgroundImage:
        step === "vipDetail"
          ? `url(${gdOssUrl(
              "https://st0.dancf.com/static/02/202205160245-1eb5.png"
            )})`
          : `url(${gdOssUrl(
              "https://st0.dancf.com/static/02/202202110238-4e8a.jpg",
              {
                width: 960,
              }
            )})`,
      backgroundSize: "cover",
    };
  }, [step]);

  const onCancelWrap = useCallback(() => {
    onCancel && onCancel(step);

    // 在支付成功页后，调用 onFinish
    if (!["vipDetail"].includes(step)) {
      onFinish && onFinish(order);
    } else {
      // 终止
      onAborted && onAborted(step);
    }
  }, [onCancel, onFinish, order, step, onAborted]);

  return (
    <HBModal
      className={styles.dialog}
      {...props}
      width={step === "vipDetail" ? 664 : 960}
      footer={null}
      bodyStyle={{ padding: 0, overflowX: "auto" }}
      onCancel={onCancelWrap}
    >
      <div style={wrapStyle}>
        <ScrollWarp
          id="vipDialogScrollWarp"
          style={{
            height: "100%",
          }}
        >
          <Switch>
            <Case condition={step === "vipDetail"}>
              <VipDetail
                good={expVipGood}
                onOrderDone={onOrderDone}
                onCancel={onCancelWrap}
              />
            </Case>
            {/* <Case condition={step === "pay"}>
              <PayPage details={details} onOrderDone={onOrderDone} />
            </Case> */}
            <Case condition={step === "payResult"}>
              <PayResult onNext={next} order={order} data-source={"体验VIP"} />
            </Case>
            <Case condition={step === "authSubject"}>
              <div className={styles.subjectWrap}>
                <h2>填写被授权方</h2>
                <AuthSubject
                  type={vipType}
                  initValue={subject}
                  scene="buyVip"
                  onSubmit={next}
                  onCancel={onCancelWrap}
                />
              </div>
            </Case>
            <Case condition={step === "subjectConfirm"}>
              <SubjectConfirm
                subject={subject}
                onBack={() => {
                  setStep("authSubject");
                }}
                onOk={onSubmit}
              />
            </Case>
          </Switch>
        </ScrollWarp>
      </div>
    </HBModal>
  );
}
